import React, { useEffect, useMemo, useState } from "react";
import {
  api,
  POOL_ALL_STATUSES,
  POOL_STATUS_META,
  type IdeaPoolAudience,
  type IdeaPoolStatus,
  type PoolIdea,
} from "../api";

type ToastFn = (kind: "success" | "error" | "info", message: string) => void;

type Props = {
  onOpenStoryboards: () => void;
  showToast: ToastFn;
};

type Filter = "all" | IdeaPoolStatus;

const AUDIENCES: IdeaPoolAudience[] = ["any", "b2c", "b2b", "affiliate", "brand"];

const AUDIENCE_LABEL: Record<IdeaPoolAudience, string> = {
  any: "Alle",
  b2c: "B2C / Endkunden",
  b2b: "B2B / Händler",
  affiliate: "Affiliate",
  brand: "Brand",
};

export const Ideas: React.FC<Props> = ({ onOpenStoryboards, showToast }) => {
  const [items, setItems] = useState<PoolIdea[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genCount, setGenCount] = useState(20);
  const [genAudience, setGenAudience] = useState<IdeaPoolAudience>("any");
  const [genTopics, setGenTopics] = useState("");
  const [genPrompt, setGenPrompt] = useState("");
  const [genResearch, setGenResearch] = useState(true);
  const [genLiveWeb, setGenLiveWeb] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setErr(null);
    try {
      const list = await api.ideasPoolList();
      setItems(list);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const counts = useMemo<Record<Filter, number>>(() => {
    const out: Record<string, number> = { all: items.length };
    for (const s of POOL_ALL_STATUSES) out[s] = 0;
    for (const it of items) out[it.status] = (out[it.status] ?? 0) + 1;
    return out as Record<Filter, number>;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filter !== "all" && it.status !== filter) return false;
      if (q && !`${it.title} ${it.hook} ${it.payoff} ${it.tags?.join(" ") ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filter, search]);

  const onSetStatus = async (id: string, status: IdeaPoolStatus): Promise<void> => {
    try {
      await api.ideasPoolSetStatus(id, status);
      await load();
    } catch (e) {
      showToast("error", `Status-Update fehlgeschlagen: ${(e as Error).message}`);
    }
  };

  const onDelete = async (id: string, title: string): Promise<void> => {
    if (!window.confirm(`Idee "${title}" wirklich löschen?`)) return;
    await api.ideasPoolDelete(id);
    await load();
  };

  const onGenerate = async (): Promise<void> => {
    setGenerating(true);
    try {
      const topics = genTopics
        .split(/[,\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await api.ideasPoolGenerate({
        count: genCount,
        audience: genAudience,
        topics: topics.length > 0 ? topics : undefined,
        prompt: genPrompt.trim() || undefined,
        research: genResearch,
        liveWeb: genLiveWeb,
      });
      showToast("success", `${res.ideas.length} Ideen generiert (${res.modelUsed}).`);
      setShowGenerate(false);
      await load();
    } catch (e) {
      showToast("error", `Generierung fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setGenerating(false);
    }
  };

  const onToStoryboard = async (idea: PoolIdea): Promise<void> => {
    setConvertingId(idea.id);
    try {
      await api.ideasPoolToStoryboard(idea.id);
      showToast("success", `Storyboard für "${idea.title}" erstellt.`);
      await load();
    } catch (e) {
      showToast("error", `Storyboard-Generierung fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setConvertingId(null);
    }
  };

  return (
    <div className="workspace">
      <div className="workspace-head">
        <div className="workspace-head-left">
          <h2>Ideen-Pool</h2>
          <p className="workspace-sub">
            Dynamische Ideen-Pipeline — KI-Recherche, Approval-Workflow, direkter Sprung ins Storyboard.
          </p>
        </div>
        <div className="workspace-head-actions">
          <input
            className="workspace-search"
            placeholder="🔍 Suche…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="btn ghost" onClick={() => void load()} disabled={loading}>
            {loading ? "…" : "↻ Refresh"}
          </button>
          <button className="btn ghost" onClick={onOpenStoryboards}>
            → Storyboards
          </button>
          <button
            className="btn primary"
            onClick={() => setShowGenerate(true)}
            disabled={generating}
          >
            ✨ {genCount} Ideen generieren
          </button>
        </div>
      </div>

      <div className="workspace-filters">
        {(["all", ...POOL_ALL_STATUSES] as Filter[]).map((f) => {
          const meta = f === "all" ? null : POOL_STATUS_META[f];
          return (
            <button
              key={f}
              className={`ws-filter ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
              style={meta ? { borderColor: meta.color } : undefined}
            >
              {meta ? <span className="ws-filter-dot" style={{ background: meta.color }} /> : null}
              <span>{f === "all" ? "Alle" : meta?.label}</span>
              <span className="ws-filter-count">{counts[f] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {err && <div className="error-row">{err}</div>}

      {filtered.length === 0 ? (
        <div className="workspace-empty">
          <div className="empty-emoji">💡</div>
          <h3>Noch keine Ideen im Pool</h3>
          <p>Generiere auf Knopfdruck einen frischen Schwung Ideen — inkl. optionaler Recherche.</p>
          <button className="btn primary" onClick={() => setShowGenerate(true)}>
            ✨ Jetzt {genCount} Ideen generieren
          </button>
        </div>
      ) : (
        <div className="idea-grid">
          {filtered.map((it) => {
            const meta = POOL_STATUS_META[it.status];
            return (
              <article key={it.id} className="idea-card">
                <span
                  className="audience"
                  style={{ borderColor: meta.color, color: meta.color }}
                >
                  {meta.label} · {it.funnel} · {it.audience}
                </span>
                <h3>{it.title}</h3>
                <p className="payoff" style={{ opacity: 0.85, marginBottom: 6 }}>
                  <strong style={{ opacity: 0.6 }}>Hook:</strong> {it.hook}
                </p>
                <p className="payoff">{it.payoff}</p>
                {it.tags && it.tags.length > 0 ? (
                  <div className="tpl-tags">
                    {it.tags.map((t) => (
                      <span key={t} className="tpl-tag">
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="meta">
                  <span style={{ opacity: 0.7 }}>{it.generatedBy}</span>
                  {typeof it.impactScore === "number" ? (
                    <span className="stars">{"★".repeat(it.impactScore)}</span>
                  ) : null}
                </div>
                <div className="card-actions" style={{ flexWrap: "wrap" }}>
                  {it.storyboardId ? (
                    <button className="btn ghost" onClick={onOpenStoryboards}>
                      Storyboard öffnen
                    </button>
                  ) : (
                    <button
                      className="btn primary"
                      onClick={() => void onToStoryboard(it)}
                      disabled={convertingId === it.id}
                    >
                      {convertingId === it.id ? "…" : "→ Storyboard"}
                    </button>
                  )}
                  {it.status === "new" ? (
                    <button
                      className="btn ghost"
                      onClick={() => void onSetStatus(it.id, "approved")}
                    >
                      ✓ Freigeben
                    </button>
                  ) : null}
                  <div style={{ display: "flex", gap: 6, flex: "0 0 auto", marginLeft: "auto" }}>
                    {it.status !== "rejected" ? (
                      <button
                        className="btn ghost"
                        onClick={() => void onSetStatus(it.id, "rejected")}
                        title="Ablehnen"
                        style={iconBtnStyle}
                      >
                        ✕
                      </button>
                    ) : null}
                    <button
                      className="btn danger"
                      onClick={() => void onDelete(it.id, it.title)}
                      title="Löschen"
                      style={iconBtnStyle}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {showGenerate ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(8,12,20,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
            padding: 24,
          }}
          onClick={() => !generating && setShowGenerate(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#0f1623",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 16,
              maxWidth: 560,
              width: "100%",
              padding: 24,
              boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
              color: "#e6edf6",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Ideen generieren</h3>
            <p style={{ opacity: 0.7, marginTop: 0 }}>
              Lässt das LLM einen frischen Batch an Ideen brainstormen. Recherche aktiviert
              ergänzt davor aktuelle Themen für die Marke.
            </p>

            <label style={{ display: "block", marginBottom: 12 }}>
              <span style={{ display: "block", opacity: 0.8, marginBottom: 4 }}>Anzahl</span>
              <input
                type="number"
                min={1}
                max={50}
                value={genCount}
                onChange={(e) => setGenCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                style={inputStyle}
              />
            </label>

            <label style={{ display: "block", marginBottom: 12 }}>
              <span style={{ display: "block", opacity: 0.8, marginBottom: 4 }}>Zielgruppe</span>
              <select
                value={genAudience}
                onChange={(e) => setGenAudience(e.target.value as IdeaPoolAudience)}
                style={inputStyle}
              >
                {AUDIENCES.map((a) => (
                  <option key={a} value={a}>
                    {AUDIENCE_LABEL[a]}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "block", marginBottom: 12 }}>
              <span style={{ display: "block", opacity: 0.8, marginBottom: 4 }}>
                Themen (optional, kommagetrennt)
              </span>
              <input
                type="text"
                value={genTopics}
                onChange={(e) => setGenTopics(e.target.value)}
                placeholder="z. B. Black Friday, KI, Affiliate"
                style={inputStyle}
              />
            </label>

            <label style={{ display: "block", marginBottom: 12 }}>
              <span style={{ display: "block", opacity: 0.8, marginBottom: 4 }}>
                Fokus / Pillar (optional)
              </span>
              <textarea
                value={genPrompt}
                onChange={(e) => setGenPrompt(e.target.value)}
                placeholder="Was soll thematisch im Vordergrund stehen?"
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={genResearch}
                onChange={(e) => setGenResearch(e.target.checked)}
              />
              <span>Recherche-Schritt davor durchführen</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={genLiveWeb}
                onChange={(e) => setGenLiveWeb(e.target.checked)}
                disabled={!genResearch}
              />
              <span>
                Live-Websuche nutzen{" "}
                <span style={{ opacity: 0.6, fontSize: 12 }}>
                  (benötigt OpenAI-Account mit web_search_preview)
                </span>
              </span>
            </label>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                className="btn ghost"
                onClick={() => setShowGenerate(false)}
                disabled={generating}
              >
                Abbrechen
              </button>
              <button
                className="btn primary"
                onClick={() => void onGenerate()}
                disabled={generating}
              >
                {generating ? "Generiere…" : `${genCount} Ideen jetzt generieren`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const iconBtnStyle: React.CSSProperties = {
  flex: "0 0 auto",
  width: 36,
  minWidth: 36,
  padding: "6px 0",
  textAlign: "center",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "#0a0f17",
  color: "#e6edf6",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 14,
  fontFamily: "inherit",
};
